import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';

const STORAGE_KEY = 'cronpay-ui-theme';

const ThemeContext = createContext(null);

const readStoredPreference = () => {
    try {
        const s = localStorage.getItem(STORAGE_KEY);
        if (s === 'light' || s === 'dark') return s;
        if (s === 'system') return 'light';
    } catch {
        /* ignore */
    }
    return 'light';
};

export function ThemeProvider({ children }) {
    const [preference, setPreferenceState] = useState(readStoredPreference);

    const resolvedTheme = preference;

    useLayoutEffect(() => {
        const root = document.documentElement;
        root.classList.toggle('dark', resolvedTheme === 'dark');
        document.body.classList.toggle('cronpay-theme-dark', resolvedTheme === 'dark');
        document.body.classList.toggle('cronpay-theme-light', resolvedTheme === 'light');
    }, [resolvedTheme]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, preference);
        } catch {
            /* ignore */
        }
    }, [preference]);

    const setPreference = useCallback((next) => {
        if (next === 'light' || next === 'dark') {
            setPreferenceState(next);
        }
    }, []);

    const value = useMemo(
        () => ({ preference, setPreference, resolvedTheme }),
        [preference, setPreference, resolvedTheme],
    );

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return ctx;
}
